import React, { useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, KeyboardArrowDown } from '@mui/icons-material';

import { encodeProfileId } from 'utils/utils';
import { UNIQUE_PROFILE_FEATURES } from 'utils/globals';
import BeSafeContainer from 'components/commons/BeSafeContainer';
import BeSafeButton from 'components/commons/BeSafeButton';
import { ProfileContext } from 'contexts/ProfileContext';
import { ConfirmationPopup } from 'components/ConfirmDialog';

const MenuConfiguration = ({ accountType, menu, parent, onChangeExpanded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { changed, setChanged } = useContext(ProfileContext);
  const intl = useIntl();

  // OT Protection is not offered to SDWAN carrier tenants.
  const { sdwanCarrier } = useSelector(
    ({ auth }) => ({ sdwanCarrier: auth.user?.sdwan_carrier === true }),
    shallowEqual
  );

  useEffect(() => {
    if (location.pathname.includes('ipsec-vpn')) {
      navigate('/');
    }
  }, [location, navigate]);

  const hasLicense = (elem) => {
    return !('has_license' in elem) || elem.has_license;
  };

  const generateKey = (elem, index) => {
    let key = `${parent.name}-${index}`;

    if (elem.id && elem.name) {
      key = `${parent.name}-${elem.name}-${elem.id}`;
    }

    if (elem.name) {
      key = `${parent.name}-${elem.name}-${index}`;
    }

    return key;
  };

  const generateName = (elem) => {
    if (elem.displayName) {
      return <FormattedMessage id={elem.displayName} />;
    }
    return elem.name;
  };

  const generateRoute = (elem) => {
    if (elem) {
      const id =
        elem.menu && UNIQUE_PROFILE_FEATURES.includes(elem.name)
          ? elem.menu[0]?.id
          : elem.id;
      const encodedId = encodeProfileId(id);

      return elem.menu && UNIQUE_PROFILE_FEATURES.includes(elem.name)
        ? `/configuration/${elem.name}/${encodedId}`
        : `/configuration/${parent.name}/${encodedId}`;
    }

    return '';
  };

  const generateTitle = (elem) => {
    if (UNIQUE_PROFILE_FEATURES.includes(elem.name)) {
      return generateName(elem);
    } else if (elem.functionality_type) {
      return `${elem.name} (${elem.functionality_type.toUpperCase()})`;
    }
    return elem.name;
  };

  const isActiveMenu = (elem) => {
    return location.pathname === generateRoute(elem);
  };

  const getFilteredElements = (elements) => {
    let filtered = elements;

    if (sdwanCarrier) {
      filtered = filtered.filter((element) => element.name !== 'ot');
    }

    if (accountType === 'carrier') {
      return filtered.filter(
        (element) =>
          ('menu' in element && 'isCarrierConf' in element) || !element.menu
      );
    }

    return filtered.filter((element) => element.name !== 'ipsec-vpn');
  };

  const generateClassName = (elem) => {
    let className = 'nav_menu';

    if (!hasLicense(elem)) {
      className += ' is-disabled';
    }

    if (elem.expanded) {
      className += ' expanded';
    }

    return className;
  };

  const hasOneProfile = (elem) => {
    return UNIQUE_PROFILE_FEATURES.includes(elem.name);
  };

  const handleProfileClick = (elem) => {
    if (changed) {
      ConfirmationPopup({
        title: intl.formatMessage({ id: 'GENERAL.WARNING' }),
        description: intl.formatMessage({
          id: 'NAVBAR.CONFIRM_DISCARD_CHANGES',
        }),
        okLabel: intl.formatMessage({ id: 'GENERAL.OK' }),
        cancelLabel: intl.formatMessage({ id: 'GENERAL.CANCEL' }),
        okAction: () => {
          navigate(generateRoute(elem));
          setChanged(false);
        },
        closeAction: () => {},
      });
      return;
    } else {
      navigate(generateRoute(elem));
      return;
    }
  };

  return (
    <>
      {getFilteredElements(menu).map((elem, index) =>
        hasOneProfile(elem) ? (
          <span
            key={generateKey(elem, index)}
            className={generateClassName(elem)}
          >
            <BeSafeButton
              className={`nav_name ${isActiveMenu(elem) ? 'is-active' : ''}`}
              text={generateName(elem)}
              icon={hasLicense(elem) ? null : <Lock fontSize="small" />}
              iconPosition="right"
              onClick={(e) => {
                e.preventDefault();
                handleProfileClick(elem);
              }}
              visible={elem.menu}
              disabled={!hasLicense(elem)}
            />
          </span>
        ) : !('menu' in elem) ? (
          <span key={generateKey(elem, index)}>
            <Link
              className={`nav_option ${isActiveMenu(elem) ? 'is-active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                handleProfileClick(elem);
              }}
            >
              {generateTitle(elem)}
            </Link>
          </span>
        ) : (
          <span
            key={generateKey(elem, index)}
            className={generateClassName(elem)}
          >
            <span className="nav_title">
              <BeSafeButton
                className="nav_name"
                text={generateName(elem)}
                icon={
                  hasLicense(elem) ? (
                    <KeyboardArrowDown className="icon" />
                  ) : (
                    <Lock fontSize="small" />
                  )
                }
                iconPosition="right"
                onClick={() => onChangeExpanded(elem)}
                visible={elem.menu}
                disabled={!hasLicense(elem)}
              />
            </span>

            <BeSafeContainer visible={hasLicense(elem)}>
              <span className="nav_options">
                <MenuConfiguration
                  accountType={accountType}
                  menu={elem.menu}
                  parent={elem}
                  onChangeExpanded={onChangeExpanded}
                />
              </span>
            </BeSafeContainer>
          </span>
        )
      )}
    </>
  );
};

export default MenuConfiguration;
